import React, { createContext, useState, useEffect, useContext, useCallback, useMemo } from 'react';
import apiClient from '../lib/apiClient';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPhoneNumber,
  signInWithPopup,
  signOut,
  updateProfile,
} from 'firebase/auth';
import { AUTH_EXPIRED_EVENT } from '../lib/authEvents';
import { getFirebaseAuth, googleAuthProvider, isFirebaseClientConfigured } from '../lib/firebaseClient';
import { formatFirebaseAuthError } from '../lib/firebaseAuthErrors';

const AuthContext = createContext(null);

function formatApiErrorDetail(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const handleAuthExpired = () => {
      setUser(false);
      setLoading(false);
    };

    if (typeof window !== 'undefined') {
      window.addEventListener(AUTH_EXPIRED_EVENT, handleAuthExpired);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener(AUTH_EXPIRED_EVENT, handleAuthExpired);
      }
    };
  }, []);

  const syncFirebaseUserToBackend = useCallback(async () => {
    if (!isFirebaseClientConfigured()) {
      return { success: false, error: 'Firebase is not configured in the client environment.' };
    }
    const auth = getFirebaseAuth();
    const fbUser = auth.currentUser;
    if (!fbUser) {
      return { success: false, error: 'Not signed in' };
    }
    const idToken = await fbUser.getIdToken();
    try {
      const { data } = await apiClient.post('/api/auth/firebase', { id_token: idToken });
      setUser(data);
      return { success: true };
    } catch (e) {
      await signOut(auth).catch(() => {});
      setUser(false);
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await apiClient.get('/api/auth/me');
      setUser(data);
    } catch (error) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const register = useCallback(async (email, password, name) => {
    if (!isFirebaseClientConfigured()) {
      return {
        success: false,
        error: 'Firebase is not configured. Set REACT_APP_FIREBASE_* in frontend/.env to enable sign-up.',
      };
    }
    try {
      const auth = getFirebaseAuth();
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      if (name?.trim() && cred.user) {
        await updateProfile(cred.user, { displayName: name.trim() });
      }
      return await syncFirebaseUserToBackend();
    } catch (e) {
      return { success: false, error: formatFirebaseAuthError(e) };
    }
  }, [syncFirebaseUserToBackend]);

  const login = useCallback(async (email, password) => {
    if (!isFirebaseClientConfigured()) {
      return {
        success: false,
        error: 'Firebase is not configured. Set REACT_APP_FIREBASE_* in frontend/.env to enable sign-in.',
      };
    }
    try {
      const auth = getFirebaseAuth();
      await signInWithEmailAndPassword(auth, email, password);
      return await syncFirebaseUserToBackend();
    } catch (e) {
      return { success: false, error: formatFirebaseAuthError(e) };
    }
  }, [syncFirebaseUserToBackend]);

  const loginWithGoogle = useCallback(async () => {
    if (!isFirebaseClientConfigured()) {
      return { success: false, error: 'Firebase is not configured in the client environment.' };
    }
    try {
      const auth = getFirebaseAuth();
      await signInWithPopup(auth, googleAuthProvider);
      return await syncFirebaseUserToBackend();
    } catch (e) {
      return { success: false, error: formatFirebaseAuthError(e) };
    }
  }, [syncFirebaseUserToBackend]);

  const startPhoneSignIn = useCallback(async (phoneNumber, appVerifier) => {
    if (!isFirebaseClientConfigured()) {
      return { success: false, error: 'Firebase is not configured in the client environment.' };
    }
    try {
      const auth = getFirebaseAuth();
      const confirmation = await signInWithPhoneNumber(auth, phoneNumber, appVerifier);
      return { success: true, confirmation };
    } catch (e) {
      return { success: false, error: formatFirebaseAuthError(e) };
    }
  }, []);

  const confirmPhoneSignIn = useCallback(
    async (confirmationResult, code) => {
      try {
        await confirmationResult.confirm(code);
        return await syncFirebaseUserToBackend();
      } catch (e) {
        return { success: false, error: formatFirebaseAuthError(e) };
      }
    },
    [syncFirebaseUserToBackend]
  );

  const logout = useCallback(async () => {
    try {
      await apiClient.post('/api/auth/logout', {});
    } catch (e) {
      console.error('Logout error:', e);
    } finally {
      if (isFirebaseClientConfigured()) {
        try {
          await signOut(getFirebaseAuth());
        } catch (e) {
          console.error('Firebase signOut error:', e);
        }
      }
      setUser(false);
    }
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await apiClient.get('/api/auth/me');
      setUser(data);
    } catch (error) {
      if (error?.response?.status === 401) {
        setUser(false);
        return;
      }
      console.error('Failed to refresh user:', error);
    }
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      register,
      login,
      logout,
      refreshUser,
      loginWithGoogle,
      startPhoneSignIn,
      confirmPhoneSignIn,
      isFirebaseClientConfigured: isFirebaseClientConfigured(),
    }),
    [user, loading, register, login, logout, refreshUser, loginWithGoogle, startPhoneSignIn, confirmPhoneSignIn]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
